"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { hoverAsCancel } from "@/styles/ItemSelectionMenu"
import { CheckSquare, Copy } from "lucide-react"
import { CalendarView } from "./calendar-view"
import { Gantt } from "./gantt/Gantt"
import { GanttListView } from "./gantt/GanttListView"
import { useGanttData } from "./gantt/useGanttData"
import { useCompanyWorkingDays } from "./useCompanyWorkingDays"
import { useProject } from "@/components/ProjectSelector"
import { trackScheduleViewed, trackButtonClick } from "@/lib/posthog-events"
import { useUser } from "@/contexts/UserContext"
import { GenericTabBar } from "@/styles/GenericTabBar"
import { StandardButton, ThreeDotMenuButton, SearchButton, DeleteRecordButton } from "@/styles/StandardButtons"
import { cn } from "@/lib/utils"

type ScheduleView = "list-view" | "gantt" | "calendar"

const VIEW_TABS: { value: ScheduleView; label: string }[] = [
  { value: "list-view", label: "List" },
  { value: "gantt", label: "Gantt" },
  { value: "calendar", label: "Calendar" },
]

const VIEW_TITLE: Record<ScheduleView, string> = {
  "list-view": "List",
  gantt: "Gantt",
  calendar: "Monthly Calendar",
}

export function SchedulingPage() {
  const [currentView, setCurrentView] = useState<ScheduleView>("calendar")
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const { selectedProject } = useProject()
  const { companyId, user } = useUser()
  const projectId = selectedProject && selectedProject !== "all" ? selectedProject : undefined

  // Single source of truth for the company's working days (Settings ->
  // Company -> Working Days), consumed by the Schedule date engine so
  // Gantt/List compute duration/end-date the exact same way.
  const { workingDays } = useCompanyWorkingDays(companyId)

  // Single Schedule data instance, shared by the Gantt timeline and List
  // View tabs so they never fetch independently or drift out of sync.
  const ganttData = useGanttData({ projectId, companyId, userId: user?.id, workingDays })

  // Gantt's Select mode + checked ids are lifted up here (not local to
  // Gantt) so the top action bar in THIS CardHeader can drive the exact
  // same selection the timeline below renders checkboxes for.
  const [ganttSelectMode, setGanttSelectMode] = useState(false)
  const [ganttCheckedIds, setGanttCheckedIds] = useState<Set<string>>(new Set())

  // Search is shared by Gantt and List (spec: same relative position,
  // next to the 3-dot menu, in both views) -- one search term, filters
  // whichever of the two is currently showing.
  const [scheduleSearch, setScheduleSearch] = useState("")

  useEffect(() => {
    trackScheduleViewed({
      view_type: currentView,
      project_id: selectedProject,
      company_id: companyId,
      page: 'schedule'
    });
  }, [currentView, selectedProject, companyId]);

  const weekDates = useMemo(() => {
    const startOfWeek = new Date(currentWeek)
    startOfWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + 1) // Monday

    const dates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      dates.push(date)
    }
    return dates
  }, [currentWeek])

  const handleTabChange = (value: string) => {
    trackButtonClick(`${value.replace(/-/g, '_')}_button`, {
      context: 'schedule',
      project_id: selectedProject,
      current_view: currentView,
    })
    setCurrentView(value as ScheduleView)
  }

  const ganttExitSelectMode = () => {
    setGanttSelectMode(false)
    setGanttCheckedIds(new Set())
  }

  const allTaskIds = useMemo(() => ganttData.tasks.filter((t) => t.type === "task").map((t) => t.id), [ganttData.tasks])
  const allSelected = allTaskIds.length > 0 && allTaskIds.every((id) => ganttCheckedIds.has(id))
  const toggleSelectAll = () => setGanttCheckedIds(allSelected ? new Set() : new Set(allTaskIds))

  const ganttBulkDelete = () => {
    if (!window.confirm(`Are you sure you want to delete ${ganttCheckedIds.size} task(s)? This action cannot be undone.`)) return
    ganttData.bulkDeleteTasks(Array.from(ganttCheckedIds))
    ganttExitSelectMode()
  }
  const ganttBulkCopy = () => {
    ganttData.bulkCopyTasks(Array.from(ganttCheckedIds))
    ganttExitSelectMode()
  }
  const ganttBulkMove = (phaseId: string) => {
    ganttData.bulkMoveToPhase(Array.from(ganttCheckedIds), phaseId)
    ganttExitSelectMode()
  }

  const ganttPhases = ganttData.phases
  const showScheduleActions = currentView === "gantt" || currentView === "list-view"

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex-shrink-0 border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-heading-large">Schedule</h1>
            {/* Same GenericTabBar Budgets uses (features/cost-management/budgets/page.tsx),
                no activeVariant override -- default 'white' active state, identical look. */}
            <GenericTabBar
              tabs={VIEW_TABS.map((t) => ({ value: t.value, label: t.label }))}
              activeTab={currentView}
              onTabChange={handleTabChange}
            />
          </div>
        </div>
      </header>

      <div className="container mx-auto flex min-h-0 flex-1 flex-col px-6 py-6">
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-gray-900">{VIEW_TITLE[currentView]}</span>

              {showScheduleActions && (
                <div className="flex flex-wrap items-center gap-2">
                  {currentView === "gantt" && ganttSelectMode && (
                    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs">
                      <StandardButton customVariant="white" onClick={toggleSelectAll} className="h-8 px-2.5 text-xs">
                        {allSelected ? "Deselect All" : "Select All"}
                      </StandardButton>
                      {ganttCheckedIds.size > 0 && <span className="font-medium text-gray-700">{ganttCheckedIds.size} selected</span>}
                      {ganttCheckedIds.size > 0 && (
                        <>
                          <StandardButton customVariant="white" onClick={ganttBulkCopy} className="h-8 px-2.5 text-xs">
                            <Copy className="mr-1.5 h-3.5 w-3.5" />
                            Copy
                          </StandardButton>
                          {ganttPhases.length > 0 && (
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) ganttBulkMove(e.target.value)
                                e.currentTarget.value = ""
                              }}
                              className="font-button h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-600 focus:outline-none"
                            >
                              <option value="" disabled>
                                Move to Phase…
                              </option>
                              {ganttPhases.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          )}
                          <StandardButton customVariant="white" onClick={() => ganttData.setBaseline(Array.from(ganttCheckedIds))} className="h-8 px-2.5 text-xs">
                            Set Baseline
                          </StandardButton>
                          {/* Destructive treatment follows ThreeDotMenuButton's own convention
                              (text-red-600, no bespoke red/orange background) -- StandardButtons.tsx. */}
                          <DeleteRecordButton label="Delete" onClick={ganttBulkDelete} className="h-8 px-2.5 py-0 text-xs" />
                        </>
                      )}
                      {/* Exact "Exit Select" pattern from Checklist's ItemSelectionMenu. */}
                      <Button type="button" variant="outline" size="sm" onClick={ganttExitSelectMode} className="h-8 px-3 bg-white border-gray-300" {...hoverAsCancel}>
                        Exit Select
                      </Button>
                    </div>
                  )}

                  {/* Search + 3-dot: same relative position in both Gantt and List (spec #5). */}
                  <SearchButton onSearch={setScheduleSearch} placeholder="Search phases & tasks..." />
                  <ThreeDotMenuButton
                    className="h-9 w-9"
                    items={
                      currentView === "gantt"
                        ? [
                            {
                              label: ganttSelectMode ? "Exit Select" : "Select",
                              icon: <CheckSquare className="h-4 w-4" />,
                              onClick: () => (ganttSelectMode ? ganttExitSelectMode() : setGanttSelectMode(true)),
                            },
                            { label: "Set Baseline (all tasks)", onClick: () => ganttData.setBaseline(allTaskIds) },
                          ]
                        : [{ label: "Set Baseline (all tasks)", onClick: () => ganttData.setBaseline(allTaskIds) }]
                    }
                  />
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className={cn("flex min-h-0 flex-1 flex-col", currentView === "calendar" && "overflow-y-auto")}>
            {currentView === "calendar" ? (
              <CalendarView weekDates={weekDates} />
            ) : currentView === "gantt" ? (
              <Gantt
                projectId={projectId}
                companyId={companyId}
                userId={user?.id}
                ganttData={ganttData}
                selectMode={ganttSelectMode}
                checkedIds={ganttCheckedIds}
                setCheckedIds={setGanttCheckedIds}
                search={scheduleSearch}
                workingDays={workingDays}
              />
            ) : (
              <GanttListView companyId={companyId} userId={user?.id} projectId={projectId} ganttData={ganttData} search={scheduleSearch} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
